import * as React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';

import DateTimeService from '@app/Services/DateTimeService';

import { DatePickerControl } from '@app/Components/DateTimeControls';
import FilterContainer from '@app/Components/Filters/FilterContainer';
import { applyDecorators } from "@app/Helpers/Decorator";

type DatePickerFilterProps = {
    className?: string;
    title: string;
    date: Date | null;
    disabled?: boolean;
    canBeEmpty?: boolean;
    minDate?: Date;
    maxDate?: Date;
    onDateChange?: (value: Date | null) => void;
};

@observer
export default class DatePickerFilter extends React.Component<DatePickerFilterProps> {
    private _store: DatePickerFilterStore;

    constructor(props: DatePickerFilterProps) {
        super(props);
        applyDecorators(this);
        this._store = new DatePickerFilterStore(this.props.date);
    }

    componentDidUpdate(prevProps: DatePickerFilterProps) {
        if (!DateTimeService.isSameDate(prevProps.date, this.props.date)) {
            if (!!this.props.date) {
                const newDate = DateTimeService.startOfDate(this.props.date);
                const dateIsValid = this._validate(newDate);

                if (dateIsValid) {
                    this._store.setDate(newDate);
                }
            } else {
                this._store.setDate(null);
            }
        }
    }

    @action.bound
    private _handleChange(time: Date, originalEventType: string) {
        const newDate = DateTimeService.startOfDate(time);
        const dateIsValid = this._validate(newDate);

        this._store.setDate(dateIsValid ? newDate : originalEventType);

        if (originalEventType === 'click' && dateIsValid) {
            this.props.onDateChange?.(newDate);
        }
    }

    @action.bound
    private _handleBlur(time: Date, value: string) {
        const { onDateChange, date, canBeEmpty } = this.props;
        const isSelectedValid = this._validate(time);
        let triggerChange: boolean = false;
        const newValue = isSelectedValid ? time : date || null;
        if (canBeEmpty) {
            this._store.currentValue = value ? newValue : null;
            triggerChange = (isSelectedValid && ((date && !DateTimeService.isSameDate(date, time)) || !date)) || (!value && !!date);
        } else {
            this._store.currentValue = newValue;
            triggerChange = isSelectedValid && !!date && !DateTimeService.isSameDate(date, time);
        }

        if (triggerChange) {
            onDateChange && onDateChange(this._store.currentValue);
        }
    }

    @action
    private _validate(time: Date) {
        const { minDate, maxDate } = this.props;
        let isValid = DateTimeService.isValidDate(time);
        if (isValid && (minDate || maxDate)) {
            if (minDate && maxDate) isValid = DateTimeService.getDateIndexInRange(time, minDate, maxDate) > -1;
            else if (minDate) isValid = DateTimeService.isAfterDate(time, minDate);
            else if (maxDate) isValid = DateTimeService.isBeforeDate(time, maxDate);
        }
        return isValid;
    }

    render() {
        const { disabled, className, title } = this.props;
        const { currentValue } = this._store;
        return (
            <FilterContainer title={title} className={`${className || ''}`}>
                <div className="datefilter">
                    <DatePickerControl disabled={disabled} onChange={this._handleChange} onBlur={this._handleBlur} onEnter={this._handleBlur} value={currentValue || ''} />
                </div>
            </FilterContainer>
        );
    }
}

class DatePickerFilterStore {
    @observable currentValue: Date | string | null = null;

    constructor(value?: Date | string | null) {
        applyDecorators(this);
        this.setDate(value);
    }

    setDate(value?: Date | string | null) {
        this.currentValue = value || null;
    }
}
